import { useEffect } from "react";

/**
 * Iter128 — L0.5 SEO sanity · per-route head management.
 * Iter130 — Made hostname-aware for the qortrace.com / qorbom.com
 *           dual-deployment architecture. Callers now pass `canonicalPath`
 *           (relative — e.g. `/trust`) and the hook resolves it against
 *           the runtime hostname so a visitor on `qorbom.com` gets a
 *           `<link rel=canonical>` pointing at `qorbom.com`, not the
 *           QorTrace flagship.
 * Iter270.1 — Audit follow-up. Hook now also writes Open Graph + Twitter
 *           Card tags so deep-link unfurls in Slack / LinkedIn / Twitter
 *           show the actual page title + description instead of the
 *           generic "QorTrace — homepage" social card from index.html.
 *
 * SPA gotcha: every public URL serves the same static index.html with the
 * same title + description + canonical. Google's renderer DOES execute
 * JavaScript and re-reads <title>, but a sizable share of crawlers
 * (LinkedIn unfurl, Bing bot, Bytespider, embed previews, sitemap
 * indexing) only read the static HTML.
 *
 * `usePageMeta()` fixes the runtime side: at every route mount it sets
 *   - document.title
 *   - <meta name="description"> content
 *   - <link rel="canonical"> href (creates the link if absent)
 *   - <meta property="og:title|description|url|image|type"> (iter270.1)
 *   - <meta name="twitter:card|title|description|image">    (iter270.1)
 *
 * For the truly-crawler-blind fallback we still need server-side meta
 * injection, but that's a separate piece of work; this hook covers
 * Google + JS-aware crawlers, which is the majority of search traffic.
 *
 * Usage — preferred (hostname-aware):
 *   usePageMeta({
 *     title:         "Trust Posture — QorTrace",
 *     description:   "QorTrace's live 9-layer security posture, refreshed every minute.",
 *     canonicalPath: "/trust",
 *   });
 *
 * Legacy (hardcoded URL still accepted for backward compat):
 *   usePageMeta({ title, description, canonical: "https://qortrace.com/trust" });
 *
 * Convention:
 *   - Titles: short, end with " — QorTrace", < 60 chars total.
 *   - Descriptions: 1 sentence, < 160 chars, hooks the click.
 *   - canonicalPath: leading slash, no query string, no trailing slash
 *     (except homepage which is "/").
 */
// Iter272.1 — Default OG image. Points at the launch-coverage card
// that is already deployed under /app/frontend/public/og-coverage-launch.png
// (134 KB PNG, content-type: image/png, qortrace.com cache HIT).
// Earlier iter270.1 default ("/og/qortrace-card-v2.png") didn't exist —
// the SPA index.html was being served as a fallback so LinkedIn /
// Slack / Twitter unfurls silently failed.
const DEFAULT_OG_IMAGE = "https://qortrace.com/og-coverage-launch.png";

export default function usePageMeta({
  title,
  description,
  canonical,
  canonicalPath,
  ogImage,
  ogType = "website",
} = {}) {
  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const previousTitle = document.title;
    const previousDesc  = getMeta("description");
    const previousCanon = getCanonical();
    const previousOgTitle = getMetaProp("og:title");
    const previousOgDesc  = getMetaProp("og:description");
    const previousOgUrl   = getMetaProp("og:url");
    const previousOgImage = getMetaProp("og:image");
    const previousOgType  = getMetaProp("og:type");
    const previousTwCard  = getMeta("twitter:card");
    const previousTwTitle = getMeta("twitter:title");
    const previousTwDesc  = getMeta("twitter:description");
    const previousTwImage = getMeta("twitter:image");

    if (title) document.title = title;
    if (description) setMeta("description", description);
    const resolvedCanonical = resolveCanonical({ canonical, canonicalPath });
    if (resolvedCanonical) setCanonical(resolvedCanonical);

    // Iter270.1 — Open Graph
    const resolvedImage = ogImage || DEFAULT_OG_IMAGE;
    if (title)               setMetaProp("og:title",       title);
    if (description)         setMetaProp("og:description", description);
    if (resolvedCanonical)   setMetaProp("og:url",         resolvedCanonical);
    if (resolvedImage)       setMetaProp("og:image",       resolvedImage);
    if (ogType)              setMetaProp("og:type",        ogType);

    // Iter270.1 — Twitter Card
    setMeta("twitter:card", "summary_large_image");
    if (title)        setMeta("twitter:title",       title);
    if (description)  setMeta("twitter:description", description);
    if (resolvedImage) setMeta("twitter:image",      resolvedImage);

    return () => {
      // Restore prior values on unmount so the next route's hook doesn't
      // see a stale value before its own effect fires.
      if (title && document.title === title && previousTitle) {
        document.title = previousTitle;
      }
      if (description && previousDesc != null) {
        setMeta("description", previousDesc);
      }
      if (resolvedCanonical && previousCanon != null) {
        setCanonical(previousCanon);
      }
      if (previousOgTitle != null) setMetaProp("og:title", previousOgTitle);
      if (previousOgDesc  != null) setMetaProp("og:description", previousOgDesc);
      if (previousOgUrl   != null) setMetaProp("og:url", previousOgUrl);
      if (previousOgImage != null) setMetaProp("og:image", previousOgImage);
      if (previousOgType  != null) setMetaProp("og:type", previousOgType);
      if (previousTwCard  != null) setMeta("twitter:card", previousTwCard);
      if (previousTwTitle != null) setMeta("twitter:title", previousTwTitle);
      if (previousTwDesc  != null) setMeta("twitter:description", previousTwDesc);
      if (previousTwImage != null) setMeta("twitter:image", previousTwImage);
    };
  }, [title, description, canonical, canonicalPath, ogImage, ogType]);
}


// --- brand-aware base URL resolution -------------------------------------

/**
 * Resolve the canonical href for the current page.
 * Priority:
 *   1. Caller passed an explicit absolute `canonical` URL → use as-is.
 *   2. Caller passed `canonicalPath` (relative) → derive from hostname.
 *
 * Hostname mapping (production):
 *   qortrace.com / www.qortrace.com       → https://qortrace.com
 *   qorbom.com   / www.qorbom.com         → https://qorbom.com
 *   *.preview.emergentagent.com (preview) → https://qortrace.com (so
 *     preview pages still canonical-redirect to production, not
 *     leak the preview URL into Google's index)
 *   anything else (localhost, custom)     → window.location.origin
 */
export function getCanonicalBase() {
  if (typeof window === "undefined") return "https://qortrace.com";
  const host = (window.location.hostname || "").toLowerCase();
  if (host === "qorbom.com" || host === "www.qorbom.com") {
    return "https://qorbom.com";
  }
  if (host === "qortrace.com" || host === "www.qortrace.com") {
    return "https://qortrace.com";
  }
  // Preview pods + any other staging host — pin to qortrace.com to keep
  // the preview URL out of search indexes.
  if (host.endsWith(".preview.emergentagent.com") || host.endsWith(".emergent.host")) {
    return "https://qortrace.com";
  }
  // Localhost / unknown — use the actual origin so dev links work.
  return window.location.origin;
}

function resolveCanonical({ canonical, canonicalPath }) {
  if (canonical) return canonical;
  if (!canonicalPath) return null;
  const base = getCanonicalBase();
  const path = canonicalPath.startsWith("/") ? canonicalPath : `/${canonicalPath}`;
  return `${base}${path}`;
}


// --- small DOM helpers ----------------------------------------------------

function getMeta(name) {
  const el = document.querySelector(`meta[name="${name}"]`);
  return el ? el.getAttribute("content") : null;
}

function setMeta(name, content) {
  const els = document.querySelectorAll(`meta[name="${name}"]`);
  if (els.length === 0) {
    const el = document.createElement("meta");
    el.setAttribute("name", name);
    el.setAttribute("content", content);
    document.head.appendChild(el);
    return;
  }
  // Update the first match, remove any duplicates so social-unfurl
  // crawlers can't pick up the stale static one from index.html.
  els[0].setAttribute("content", content);
  for (let i = 1; i < els.length; i += 1) {
    els[i].parentNode && els[i].parentNode.removeChild(els[i]);
  }
}

function getMetaProp(property) {
  const el = document.querySelector(`meta[property="${property}"]`);
  return el ? el.getAttribute("content") : null;
}

function setMetaProp(property, content) {
  const els = document.querySelectorAll(`meta[property="${property}"]`);
  if (els.length === 0) {
    const el = document.createElement("meta");
    el.setAttribute("property", property);
    el.setAttribute("content", content);
    document.head.appendChild(el);
    return;
  }
  els[0].setAttribute("content", content);
  for (let i = 1; i < els.length; i += 1) {
    els[i].parentNode && els[i].parentNode.removeChild(els[i]);
  }
}

function getCanonical() {
  const el = document.querySelector('link[rel="canonical"]');
  return el ? el.getAttribute("href") : null;
}

function setCanonical(href) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}
